// components/modals/ModalProvider.jsx
import React, { createContext, useContext, useState } from 'react'
import ScheduleAppointment from './modals/ScheduleAppointment'
import AddLifeInsurance from './modals/AddLifeInsurance'
import AddAccounts from './modals/AddAccounts'
import EditAccount from './modals/EditAccount'
import Referral from './modals/Referral'
import WillSimple from './modals/WillSimple'
import FallbackModal from './modals/FallbackModal'
import QuestionModal from './modals/QuestionModal'
import AnnuityRecommenderModal from './modals/AnnuityRecommenderModal'
import FieldModal from './modals/FieldModal'
import CreateUserModal from './modals/CreateUserModal'
import AuthHelp from './modals/AuthHelp'
import RothSimple from './modals/RothSimple'
import LifestyleGoalModal from './modals/LifestyleGoalModal'
import RiskAssessmentQuizModal from './modals/RiskAssessmentQuizModal'
import RiskProfileModal from './modals/RiskProfileModal'
import AddChildModal from './modals/AddChild'
import RetirementSolverModal from './modals/RetirementSolverModal'
import CreateOpportunityModal from './modals/CreateOpportunityModal'
import ContributeTSP from './modals/todoItems/ContributeTSP'
import { TesterSignup } from './modals/todoItems/TesterSignup'
import WebinarSignup from './modals/WebinarSignup'

const ModalContext = createContext()

export function useModal() {
  return useContext(ModalContext)
}

export function ModalProvider({ children }) {
  // ⬇️ Use a stack instead of a single modal
  const [modalStack, setModalStack] = useState([])

  // Push a new modal onto the stack
  const openModal = (name, props = {}) => {
    setModalStack((prev) => [...prev, { name, props }])
  }

  // Pop the top modal off the stack
  const closeModal = () => {
    setModalStack((prev) => prev.slice(0, -1))
  }

  // Current (topmost) modal on the stack
  const currentModal = modalStack[modalStack.length - 1]

  // Helper to render a given modal by name
  const renderModal = (name, props) => {
    const modalProps = { ...props, onClose: closeModal }

    switch (name) {
      case 'scheduleAppointment':
        return <ScheduleAppointment {...modalProps} />
      case 'addLifeInsurance':
        return <AddLifeInsurance {...modalProps} />
      case 'addChild':
        return <AddChildModal {...modalProps} />
      case 'addAccounts':
        return <AddAccounts {...modalProps} />
      case 'annuityRecommender':
        return <AnnuityRecommenderModal {...modalProps} />
      case 'retirementSolver':
        return <RetirementSolverModal {...modalProps} />
      case 'lifestyleGoal':
        return <LifestyleGoalModal {...modalProps} />
      case 'riskAssessmentQuiz':
        return <RiskAssessmentQuizModal {...modalProps} />
      case 'riskProfileModal':
        return <RiskProfileModal {...modalProps} />
      case 'openRothIRA':
        return <RothSimple {...modalProps} />
      case 'trustAndWill':
        return <AddAccounts {...modalProps} />
      case 'willSimple':
        return <WillSimple {...modalProps} />
      case 'questionModal':
        return <QuestionModal {...modalProps} />
      case 'editAccount':
        return <EditAccount {...modalProps} />
      case 'fieldModal':
        return <FieldModal {...modalProps} />
      case 'createUserModal':
        return <CreateUserModal {...modalProps} />
      case 'createOpportunityModal':
        return <CreateOpportunityModal {...modalProps} />
      case 'fallbackModal':
        return <FallbackModal {...modalProps} />
      case 'referral':
        return <Referral {...modalProps} />
      case 'authHelp':
        return <AuthHelp {...modalProps} />
      case 'contributeTSP':
        return <ContributeTSP {...modalProps} />
      case 'testerSignup':
        return <TesterSignup {...modalProps} />
      case 'webinarSignup':
        return <WebinarSignup {...modalProps} />
      default:
        return null
    }
  }

  return (
    <ModalContext.Provider value={{ openModal, closeModal }}>
      {children}

      {/* Render all modals in the stack */}
      {modalStack.map(({ name, props }, index) => (
        <React.Fragment key={index}>{renderModal(name, props)}</React.Fragment>
      ))}
    </ModalContext.Provider>
  )
}
